import {
  createContext,
  useContext,
  useEffect,
  useSyncExternalStore,
  useState,
  type ReactNode,
} from "react";

import { useAuthenticatedMedia } from "~/hooks/useAuthenticatedMedia";
import { useThreadsFeed } from "~/hooks/useThreadsFeed";
import { getInitials } from "~/lib/initials";
import { mobileStrings } from "~/components/mobile/strings";
import type { Client, ThreadSummary, TimelineEventContent } from "~/lib/matrix";
import { messageContentBody } from "~/lib/message-utils";

const AVATAR_SIZE = 16;
const DOT_SIZE = 6;

export interface ThreadUnreadStore {
  get(rootId: string): number;
  replace(next: ReadonlyMap<string, number>): void;
  subscribe(listener: () => void): () => void;
}

export function createThreadUnreadStore(): ThreadUnreadStore {
  const counts = new Map<string, number>();
  const listeners = new Set<() => void>();
  return {
    get(rootId) {
      return counts.get(rootId) ?? 0;
    },
    replace(next) {
      let changed = false;
      for (const [rootId, count] of next) {
        if ((counts.get(rootId) ?? 0) !== count) {
          changed = true;
          break;
        }
      }
      if (!changed) {
        for (const rootId of counts.keys()) {
          if (!next.has(rootId)) {
            changed = true;
            break;
          }
        }
      }
      if (!changed) return;
      counts.clear();
      for (const [rootId, count] of next) counts.set(rootId, count);
      for (const listener of listeners) listener();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

const EMPTY_STORE: ThreadUnreadStore = Object.freeze({
  get: () => 0,
  replace: () => {},
  subscribe: () => () => {},
});

export const ThreadUnreadContext = createContext<ThreadUnreadStore>(EMPTY_STORE);

export function useThreadUnread(rootId: string): number {
  const store = useContext(ThreadUnreadContext);
  return useSyncExternalStore(store.subscribe, () => store.get(rootId));
}

export function ThreadUnreadHost({ children }: { children: ReactNode }) {
  const [store] = useState(createThreadUnreadStore);
  const { items } = useThreadsFeed();
  useEffect(() => {
    const next = new Map<string, number>();
    for (const item of items) {
      if (item.unreadReplyCount > 0) next.set(item.rootEventId, item.unreadReplyCount);
    }
    store.replace(next);
  }, [items, store]);
  return <ThreadUnreadContext.Provider value={store}>{children}</ThreadUnreadContext.Provider>;
}

export function threadCountLabel(replyCount: number, unread: number): string {
  const replies = replyCount === 1 ? "1 reply" : `${replyCount} replies`;
  return unread > 0 ? `${replies} \u00b7 ${unread} new` : replies;
}

function TinyAvatar({
  client,
  avatarUrl,
  name,
}: {
  client: Client | null;
  avatarUrl: string | null;
  name: string;
}) {
  const mxc = avatarUrl?.startsWith("mxc://") ? avatarUrl : null;
  const media = useAuthenticatedMedia(client, mxc, {
    reuseAvatar: true,
    thumbnail: { w: AVATAR_SIZE * 2, h: AVATAR_SIZE * 2, method: "crop" },
  });
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const src = media.src && media.src !== failedSrc ? media.src : null;
  return (
    <span
      aria-hidden
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: AVATAR_SIZE,
        height: AVATAR_SIZE,
        flexShrink: 0,
        borderRadius: "var(--chat-radius-full)",
        background: "var(--chat-surface-raised)",
        color: "var(--chat-fg-secondary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: 8,
        overflow: "hidden",
      }}
    >
      {src ? (
        <img
          src={src}
          alt=""
          onError={() => setFailedSrc(src)}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        getInitials(name).slice(0, 1)
      )}
    </span>
  );
}

/**
 * Touch summary under a thread root: what was said last, then how many
 * replies and how many are new. One target opens the thread.
 */
/** What the last reply says; media reads as its kind, a file as its name. */
function tailText(content: TimelineEventContent): string {
  if (content.type === "m.image") return mobileStrings.thread.photo;
  if (content.type === "m.video") return mobileStrings.thread.video;
  if (content.type === "m.audio") return mobileStrings.thread.audio;
  return messageContentBody(content) ?? "";
}

export function ThreadTail({
  summary,
  client,
  unread,
  onOpen,
}: {
  summary: ThreadSummary;
  client: Client | null;
  unread: number;
  onOpen: () => void;
}) {
  const last = summary.lastReply;
  if (summary.replyCount === 0) return null;
  const name = last ? last.sender.displayName || last.sender.id : "";
  const body = last ? tailText(last.content) : "";
  return (
    <button
      type="button"
      onClick={onOpen}
      className="chat-button"
      data-testid="thread-tail"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        width: "100%",
        minHeight: 44,
        marginTop: "var(--chat-space-1)",
        padding: "var(--chat-space-1) 0",
        border: "none",
        background: "transparent",
        textAlign: "left",
        cursor: "pointer",
        fontFamily: "var(--chat-font-sans)",
        fontWeight: 300,
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--chat-space-2)",
          minWidth: 0,
          minHeight: "var(--chat-type-body-xs-line)",
          width: "100%",
        }}
      >
        {last && <TinyAvatar client={client} avatarUrl={last.sender.avatarUrl} name={name} />}
        <span
          style={{
            flexShrink: 1,
            minWidth: 0,
            maxWidth: "40%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "hsl(var(--foreground) / 0.7)",
            fontSize: "var(--chat-type-body-xs-size)",
            lineHeight: "var(--chat-type-body-xs-line)",
          }}
        >
          {name}
        </span>
        <span
          data-feedback-redact="true"
          style={{
            minWidth: 0,
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: unread > 0 ? "var(--chat-fg-primary)" : "var(--chat-fg-secondary)",
            fontSize: "var(--chat-type-body-xs-size)",
            lineHeight: "var(--chat-type-body-xs-line)",
          }}
        >
          {body}
        </span>
      </span>
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--chat-space-2)",
          color: "hsl(var(--secondary-text))",
          fontSize: "var(--chat-type-body-xs-size)",
          lineHeight: "var(--chat-type-body-xs-line)",
        }}
      >
        <span
          aria-hidden
          style={{
            display: "inline-flex",
            justifyContent: "center",
            width: AVATAR_SIZE,
            flexShrink: 0,
          }}
        >
          {unread > 0 && (
            <span
              style={{
                width: DOT_SIZE,
                height: DOT_SIZE,
                borderRadius: "var(--chat-radius-full)",
                background: "hsl(var(--secondary))",
              }}
            />
          )}
        </span>
        {threadCountLabel(summary.replyCount, unread)}
      </span>
    </button>
  );
}
